import { useState } from 'react';
import { Alert, Modal, Select, message } from 'antd';
import { Permission } from '@aid/shared';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useRolesList } from '../../roles/api/useRoles';
import { useAssignRole } from '../api/useUsers';
import { useT } from '../../../locales';

interface AssignRoleModalProps {
  userId: string;
  open: boolean;
  onClose: () => void;
}

export function AssignRoleModal({ userId, open, onClose }: AssignRoleModalProps) {
  const t = useT();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const canReadRoles = useHasPermission(Permission.ROLES_READ);
  const { data: rolesData, isLoading: rolesLoading } = useRolesList(
    { limit: 100, isActive: true },
    { enabled: canReadRoles },
  );
  const assignRole = useAssignRole();

  const handleOk = async () => {
    if (!canReadRoles) return;

    if (selectedRoles.length === 0) {
      message.warning(t('pages.users.rolesRequired'));
      return;
    }
    try {
      await assignRole.mutateAsync({ id: userId, roleIds: selectedRoles });
      message.success(t('pages.users.rolesSuccess'));
      setSelectedRoles([]);
      onClose();
    } catch {
      message.error(t('pages.users.rolesError'));
    }
  };

  const handleCancel = () => {
    setSelectedRoles([]);
    onClose();
  };

  return (
    <Modal
      title={t('pages.users.assignRoles')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={assignRole.isPending}
      destroyOnHidden
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      okButtonProps={{ disabled: !canReadRoles }}
    >
      {canReadRoles ? (
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder={t('pages.users.rolesPlaceholder')}
          loading={rolesLoading}
          value={selectedRoles}
          onChange={setSelectedRoles}
          options={(rolesData?.data ?? [])
            .filter((r) => r.isActive)
            .map((r) => ({
              label: r.name,
              value: r.id,
            }))}
        />
      ) : (
        <Alert
          type="info"
          showIcon
          title={t('pages.users.rolesUnavailableTitle')}
          description={t('pages.users.rolesUnavailableDescription')}
        />
      )}
    </Modal>
  );
}
